import { fetchData, uploadImage } from "@axios/axios-client";
import { resolveUploadableImage } from "@utils/reupload-images";

const withOptionalImage = (data: Record<string, any>) => {
  const payload = { ...data };
  const image = resolveUploadableImage(payload.image);
  if (image) {
    payload.image = image;
  } else {
    delete payload.image;
  }
  return payload;
};

export const getAssetsApi = async () => {
  return fetchData({}, `/assets`, "GET");
};

export const updateAssetsApi = async ({
  image,
  payload,
  assetId,
}: {
  image?: any;
  payload: any;
  assetId: string;
}) => {
  const body: Record<string, any> = {
    payload: JSON.stringify(payload),
  };
  const parsedImage = resolveUploadableImage(image);
  if (parsedImage) {
    body.image = parsedImage;
  }
  return await fetchData(body, `/assets/${assetId}`, "PATCH", false, true);
};

export const linkAssetApi = async ({
  activityId,
  assetId,
}: {
  activityId: string;
  assetId: string;
}) => {
  return await fetchData(
    {
      assetId,
      assetActivityId: activityId,
    },
    `/assets-activities/${activityId}/assets`,
    "POST",
  );
};

export const createAssetsApi = async ({ data }: { data: any }) => {
  return await fetchData(
    withOptionalImage(data),
    `/assets`,
    "POST",
    false,
    true,
  );
};

export const createAssetActivityApi = async ({ data }: any) => {
  return fetchData(
    withOptionalImage(data),
    `/assets-activities`,
    "POST",
    false,
    true,
  );
};

export const updateAssetActivityApi = async ({
  data,
  activityId,
}: {
  data: any;
  activityId: string;
}) => {
  const { image, ...rest } = withOptionalImage(data);
  if (rest.settings && typeof rest.settings === "object") {
    rest.settings = Object.fromEntries(
      Object.entries(rest.settings).filter(([, value]) => value !== null),
    );
  }
  const body: Record<string, any> = {
    payload: JSON.stringify(rest),
  };
  const parsedImage = resolveUploadableImage(image);
  if (parsedImage) {
    body.image = parsedImage;
  }
  return fetchData(
    body,
    `/assets-activities/${activityId}`,
    "PATCH",
    false,
    true,
  );
};

export const bulkUpdateAssetActivitiesApi = async ({ data }: { data: any }) => {
  return fetchData(data, `/assets-activities`, "PUT");
};

export const getAssetsActivitiesApi = async () => {
  return fetchData({}, "/assets-activities", "GET");
};

export const getAssetsActivityApi = async (id: string) => {
  return fetchData({}, `/assets-activities/${id}`, "GET");
};

export const getAssetsActivityAssetsApi = async (id: string) => {
  return fetchData({}, `/assets-activities/${id}/assets`, "GET");
};

export const deleteAssetsActivityApi = async (id: string) => {
  return fetchData({}, `/assets-activities/${id}/details`, "DELETE");
};

export const deleteAssetActivityApi = async (id: string) => {
  return fetchData({}, `/assets-activities/${id}`, "DELETE");
};

export const createAssetActivityGalleryApi = async (
  data: any[],
  activityId: string,
) => {
  const formData = new FormData();
  data.forEach((file: any) => {
    formData.append("files", file);
  });
  return uploadImage({
    data: formData,
    url: `/assets-activities/${activityId}/gallery`,
    method: "POST",
    contentType: true,
  });
};

export const updateAssetActivityGalleryApi = async ({
  data,
  activityId,
}: {
  data: any;
  activityId: string;
}) => {
  return uploadImage({
    data,
    url: `/assets-activities/${activityId}/gallery`,
    method: "PUT",
    contentType: false,
  });
};

export const deleteAssetActivityGalleryApi = async ({
  data,
  activityId,
}: {
  data: any;
  activityId: string;
}) => {
  return uploadImage({
    data,
    url: `/assets-activities/${activityId}/gallery`,
    method: "PATCH",
    contentType: false,
  });
};

export const getAssetsActivitiesTypesApi = async () => {
  return fetchData({}, "/assets-activities-types", "GET");
};

export const deleteAssetActivitiesApi = async (
  id: string,
  activityId: string,
) => {
  return fetchData(
    {},
    `/assets-activities/${activityId}/assets/${id}`,
    "DELETE",
  );
};

export const getAssetsBookingsApi = async ({
  activityId,
  queryParams,
}: {
  activityId: string;
  queryParams: Record<string, any>;
}) => {
  return fetchData(
    queryParams,
    `/assets-activities/${activityId}/booked-slots`,
    "GET",
    true,
  );
};

export type CreateAssetsActivityBookingPayload = {
  paymentMethod: string;
  bookedBy: string;
  bookedFor: string;
  assetsSlotsIds: string[];
  walletTxnId: string;
};

export const createAssetsActivityBookingApi = async ({
  activityId,
  data,
}: {
  activityId: string;
  data: CreateAssetsActivityBookingPayload;
}) => {
  return fetchData(
    data,
    `/assets-activities/${activityId}/bookings`,
    "POST",
    false,
    false,
  );
};

export const getAssetSlotsApi = async ({
  assetId,
  queryParams,
}: {
  assetId: string;
  queryParams: { startDate: string; endDate: string };
}) => {
  return fetchData(queryParams, `/assets/${assetId}/slots`, "GET", true);
};

export const exportAssetsBookingsApi = async ({
  activityId,
  queryParams,
}: {
  activityId: string;
  queryParams: Record<string, any>;
}) => {
  return fetchData(
    queryParams,
    `/assets-activities/${activityId}/booked-slots/export`,
    "GET",
    true,
  );
};

export type CancelAssetsActivityBookedSlotPayload = {
  cancellationReason: string;
  refundedAmount: number;
};

export const cancelAssetsActivityBookedSlotApi = async ({
  activityId,
  bookedSlotLineId,
  data,
}: {
  activityId: string;
  bookedSlotLineId: string;
  data: CancelAssetsActivityBookedSlotPayload;
}) => {
  return fetchData(
    {
      cancellationReason: data.cancellationReason,
      refundedAmount: data.refundedAmount,
    },
    `/assets-activities/${activityId}/booked-slots/${bookedSlotLineId}/cancel`,
    "PATCH",
  );
};

export const getAssetsActivityBookedSlotApi = async ({
  activityId,
  bookedSlotLineId,
}: {
  activityId: string;
  bookedSlotLineId: string;
}) => {
  return fetchData(
    {},
    `/assets-activities/${activityId}/booked-slots/${bookedSlotLineId}`,
    "GET",
    true,
  );
};

export const getSingleAssetBookingApi = async (id: string) => {
  const res = await fetchData({}, `/assets/bookings/${id}`, "GET", true);
  return res.booking;
};

export const getBookingCancellationFeesApi = async ({
  bookingId,
  assetSlotId,
  activityId,
}: {
  bookingId: string;
  assetSlotId: string;
  activityId: string;
}) => {
  return fetchData(
    {},
    `/assets-activities/${activityId}/bookings/${bookingId}/slots/${assetSlotId}/cancellation-fees`,
    "GET",
    true,
  );
};

export const deleteAssetApi = async (id: string) => {
  return fetchData({}, `/assets/${id}`, "DELETE");
};
